import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { useStudentFocusSession } from '../../contexts/StudentFocusSessionContext';
import FullscreenNumberDisplay from '../../components/student/FullscreenNumberDisplay';
import DailyExercisesHero from '../../components/student/dailyExercises/DailyExercisesHero';
import { ZapIcon, PlayIcon, CheckCircleIcon, XCircleIcon } from 'lucide-react';
import api from '../../services/api';
import toast from 'react-hot-toast';

// المستويات 1، 2، 3: الجمع والمختلط فقط. المستوى 4+: جميع العمليات
const BEGINNER_LEVELS = [1, 2, 3];

const kidFieldLabel =
  'mb-1 block text-[11px] font-bold uppercase tracking-wide text-slate-500 sm:text-xs';
const kidInputClass =
  'h-9 w-full rounded-xl border-2 border-sky-200 bg-white px-2.5 text-xs font-semibold text-slate-800 shadow-inner transition focus:border-sky-400 focus:outline-none focus:ring-2 focus:ring-sky-200/80 sm:text-sm';

const FlashAnzan = () => {
  const { t } = useTranslation();
  const { isRTL } = useLanguage();
  const { user } = useAuth();
  const { setFocusSession } = useStudentFocusSession();

  // إعدادات الجلسة
  const [sessionSettings, setSessionSettings] = useState({
    totalNumbers: '3',
    digitsPerNumber: '1',
    displayTime: '1500',
    operationType: 'addition',
    totalRounds: '3',
    difficulty: 'medium' // 'easy', 'medium', 'hard'
  });

  // حالة شاشة اللعب
  const [gameState, setGameState] = useState('settings'); // 'settings', 'ready', 'countdown', 'playing', 'answer', 'result', 'roundComplete', 'break'
  const [trafficLightIndex, setTrafficLightIndex] = useState(0);
  const [breakCountdown, setBreakCountdown] = useState(null);
  const [breakPaused, setBreakPaused] = useState(false);
  const [sessionData, setSessionData] = useState(null);
  const [currentNumberIndex, setCurrentNumberIndex] = useState(0);
  const [studentAnswer, setStudentAnswer] = useState('');
  const [result, setResult] = useState(null);
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  // حالة الجولات المتتالية
  const [currentRound, setCurrentRound] = useState(1);
  const [roundResults, setRoundResults] = useState([]);
  const [allSessionsData, setAllSessionsData] = useState([]);

  // حالة الإحصائيات العامة
  const [overallStats, setOverallStats] = useState({
    totalSessions: 0,
    correctSessions: 0,
    avgScore: 0,
    avgTime: 0,
    bestScore: 0,
    worstScore: 0,
    totalScore: 0,
    accuracyPercentage: 0
  });
  const [loadingStats, setLoadingStats] = useState(false);

  // حالة جدول الأرشيف
  const [archiveData, setArchiveData] = useState([]);
  const [loadingArchive, setLoadingArchive] = useState(false);
  const [archivePagination, setArchivePagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCount: 0,
    limit: 10,
    hasNextPage: false,
    hasPrevPage: false
  });

  // مستوى الطالب من /auth/me (user.level.levelNumber)
  const studentLevelNumber = user?.level?.levelNumber != null ? parseInt(user.level.levelNumber, 10) : null;
  const disableOtherOperations = studentLevelNumber != null && BEGINNER_LEVELS.includes(studentLevelNumber);

  // Refs للتحكم في الأصوات والتوقيت
  const audioContextRef = useRef(null);
  const intervalRef = useRef(null);
  const trafficLightTimeoutsRef = useRef([]);
  const breakCountdownIntervalRef = useRef(null);
  const breakPausedRef = useRef(false);
  const pendingNextRoundRef = useRef(null);

  useEffect(() => {
    setFocusSession(gameState !== 'settings');
    return () => setFocusSession(false);
  }, [gameState, setFocusSession]);

  // إنشاء صوت التنبيه
  const playBeepSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      oscillator.frequency.setValueAtTime(800, audioContextRef.current.currentTime);
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.1);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.1);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت التصفيق للإجابة الصحيحة
  const playSuccessSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // محاكاة صوت التصفيق
      const clapCount = 6;
      const clapInterval = 0.15;
      
      for (let i = 0; i < clapCount; i++) {
        // نغمة عالية سريعة للتصفيق
        const oscillator = audioContextRef.current.createOscillator();
        const gainNode = audioContextRef.current.createGain();
        
        oscillator.connect(gainNode);
        gainNode.connect(audioContextRef.current.destination);
        
        // تردد عالي مع تشويش لمحاكاة التصفيق
        oscillator.frequency.setValueAtTime(2000 + Math.random() * 1000, audioContextRef.current.currentTime + i * clapInterval);
        oscillator.type = 'square';
        
        gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime + i * clapInterval);
        gainNode.gain.linearRampToValueAtTime(0.3, audioContextRef.current.currentTime + i * clapInterval + 0.02);
        gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + i * clapInterval + 0.1);
        
        oscillator.start(audioContextRef.current.currentTime + i * clapInterval);
        oscillator.stop(audioContextRef.current.currentTime + i * clapInterval + 0.1);
      }
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت نقرة لإشارة المرور
  const playClickSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      oscillator.frequency.setValueAtTime(1200, audioContextRef.current.currentTime);
      oscillator.type = 'sine';
      gainNode.gain.setValueAtTime(0.2, audioContextRef.current.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 0.08);
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 0.08);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // صوت "Ohhhhh" للإجابة الخاطئة
  const playEncouragementSound = () => {
    try {
      if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
        audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();
      }
      
      // محاكاة صوت "Ohhhhh" - نغمة منخفضة ممتدة
      const oscillator = audioContextRef.current.createOscillator();
      const gainNode = audioContextRef.current.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContextRef.current.destination);
      
      // نغمة منخفضة مع تذبذب لمحاكاة "Ohhhhh"
      oscillator.frequency.setValueAtTime(150, audioContextRef.current.currentTime); // نغمة منخفضة
      oscillator.frequency.setValueAtTime(120, audioContextRef.current.currentTime + 0.3); // تنخفض أكثر
      oscillator.frequency.setValueAtTime(100, audioContextRef.current.currentTime + 0.6); // تنخفض أكثر
      oscillator.type = 'sawtooth'; // صوت أكثر خشونة
      
      gainNode.gain.setValueAtTime(0, audioContextRef.current.currentTime);
      gainNode.gain.linearRampToValueAtTime(0.2, audioContextRef.current.currentTime + 0.1);
      gainNode.gain.linearRampToValueAtTime(0.15, audioContextRef.current.currentTime + 0.4);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContextRef.current.currentTime + 1.2);
      
      oscillator.start(audioContextRef.current.currentTime);
      oscillator.stop(audioContextRef.current.currentTime + 1.2);
    } catch (error) {
      console.log('Audio not supported');
    }
  };

  // معالجة تغيير الإعدادات
  const handleSettingChange = (key, value) => {
    setSessionSettings(prev => ({
      ...prev,
      [key]: value
    }));
  };

  // إنشاء الجلسة الأولى
  const createSession = async () => {
    setIsLoading(true);
    try {
      const response = await api.post('/education/flash-anzan/sessions', {
        totalNumbers: parseInt(sessionSettings.totalNumbers),
        digitsPerNumber: parseInt(sessionSettings.digitsPerNumber),
        displayTimeMs: parseInt(sessionSettings.displayTime),
        operationType: sessionSettings.operationType,
        difficulty: sessionSettings.difficulty
      });

      if (response.data.success) {
        setSessionData(response.data.data);
        setCurrentRound(1);
        setRoundResults([]);
        setAllSessionsData([response.data.data]);
        setGameState('ready');
        toast.success(response.data.message);
        // تحديث الأرشيف بعد إنشاء جلسة جديدة
        fetchArchiveData();
      }
    } catch (error) {
      console.error('Error creating session:', error);
      toast.error(error.response?.data?.message || 'Failed to create session');
    } finally {
      setIsLoading(false);
    }
  };

  // عرض الأرقام بالتسلسل
  const startNumberDisplay = (data) => {
    const session = data || sessionData;
    if (!session?.numbers) return;

    setSessionStartTime(Date.now());
    let index = 0;
    intervalRef.current = setInterval(() => {
      if (index < session.numbers.length) {
        setCurrentNumberIndex(index);
        playBeepSound();
        index++;
      } else {
        clearInterval(intervalRef.current);
        setGameState('answer');
      }
    }, session.displayTime);
  };

  // بدء الجلسة (مع إشارة مرور للجولة الأولى)
  const startSession = async () => {
    if (!sessionData) return;

    setIsLoading(true);
    try {
      const response = await api.post(`/education/flash-anzan/sessions/${sessionData.sessionId}/start`);
      
      if (response.data.success) {
        const data = response.data.data;
        setSessionData(data);
        setCurrentNumberIndex(0);
        setIsLoading(false);

        // الجولة الأولى فقط: إشارة مرور قبل بدء الأرقام
        if (currentRound === 1) {
          setTrafficLightIndex(0);
          setGameState('countdown');
          trafficLightTimeoutsRef.current.forEach((t) => clearTimeout(t));
          trafficLightTimeoutsRef.current = [];

          const INTERVAL = 1000;
          const FIRST_DELAY = 1150;
          const t1 = setTimeout(() => {
            setTrafficLightIndex(1);
            playClickSound();
          }, INTERVAL);
          const t2 = setTimeout(() => {
            setTrafficLightIndex(2);
            playClickSound();
          }, INTERVAL + FIRST_DELAY);
          const t3 = setTimeout(() => {
            setTrafficLightIndex(3);
            playClickSound();
          }, INTERVAL + FIRST_DELAY + INTERVAL);
          const t4 = setTimeout(() => {
            trafficLightTimeoutsRef.current = [];
            setGameState('playing');
            startNumberDisplay(data);
          }, INTERVAL + FIRST_DELAY + INTERVAL * 2);

          trafficLightTimeoutsRef.current = [t1, t2, t3, t4];
        } else {
          setGameState('playing');
          startNumberDisplay(data);
        }
      }
    } catch (error) {
      console.error('Error starting session:', error);
      toast.error(error.response?.data?.message || 'Failed to start session');
      setIsLoading(false);
    }
  };

  // إرسال الإجابة
  const submitAnswer = async () => {
    if (!sessionData || !studentAnswer) return;

    setIsLoading(true);
    const totalTimeMs = Date.now() - sessionStartTime;

    try {
      const response = await api.post(`/education/flash-anzan/sessions/${sessionData.sessionId}/submit`, {
        studentAnswer: parseInt(studentAnswer),
        totalTimeMs: totalTimeMs
      });

      if (response.data.success) {
        const roundResult = response.data.data;
        setResult(roundResult);
        
        // حفظ نتيجة الجولة الحالية
        setRoundResults(prev => [...prev, roundResult]);
        
        // التحقق من انتهاء جميع الجولات
        const totalRounds = parseInt(sessionSettings.totalRounds);
        if (currentRound >= totalRounds) {
          setGameState('result');
          toast.success(t('competitions.allRoundsCompleted'));
        } else if (totalRounds >= 10 && currentRound % 10 === 0) {
          // استراحة بعد كل 10 جولات (بدون صوت)
          pendingNextRoundRef.current = currentRound + 1;
          breakPausedRef.current = false;
          setBreakPaused(false);
          setBreakCountdown(6);
          setGameState('break');
        } else {
          // استمرار مباشرة للجولة التالية دون عرض نتيجة الجولة
          setStudentAnswer('');
          setResult(null);
          createNextSession(currentRound + 1);
        }
        
        // تحديث الأرشيف
        fetchArchiveData();
        fetchOverallStatistics();
      }
    } catch (error) {
      console.error('Error submitting answer:', error);
      toast.error(error.response?.data?.message || 'Failed to submit answer');
    } finally {
      setIsLoading(false);
    }
  };

  // بدء الجولة التالية بعد الاستراحة
  const startNextRoundAfterBreak = () => {
    const nextRound = pendingNextRoundRef.current;
    if (nextRound == null) return;
    if (breakCountdownIntervalRef.current) {
      clearInterval(breakCountdownIntervalRef.current);
      breakCountdownIntervalRef.current = null;
    }
    setBreakCountdown(null);
    setBreakPaused(false);
    breakPausedRef.current = false;
    pendingNextRoundRef.current = null;
    setCurrentRound(nextRound);
    setStudentAnswer('');
    setResult(null);
    createNextSession(nextRound);
  };

  // عد تنازلي الاستراحة
  useEffect(() => {
    if (gameState !== 'break' || breakPaused || breakCountdown == null) return;
    breakPausedRef.current = false;
    breakCountdownIntervalRef.current = setInterval(() => {
      setBreakCountdown((prev) => {
        if (prev <= 1) {
          if (breakCountdownIntervalRef.current) {
            clearInterval(breakCountdownIntervalRef.current);
            breakCountdownIntervalRef.current = null;
          }
          if (!breakPausedRef.current) {
            setTimeout(() => startNextRoundAfterBreak(), 0);
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
    return () => {
      if (breakCountdownIntervalRef.current) {
        clearInterval(breakCountdownIntervalRef.current);
        breakCountdownIntervalRef.current = null;
      }
    };
  }, [gameState, breakPaused]);

  // إنشاء جلسة جديدة للجولة التالية وبدءها مباشرة
  const createNextSession = async (nextRoundNum) => {
    if (nextRoundNum != null) {
      setCurrentRound(nextRoundNum);
    }
    setIsLoading(true);
    try {
      const response = await api.post('/education/flash-anzan/sessions', {
        totalNumbers: parseInt(sessionSettings.totalNumbers),
        digitsPerNumber: parseInt(sessionSettings.digitsPerNumber),
        displayTimeMs: parseInt(sessionSettings.displayTime),
        operationType: sessionSettings.operationType,
        difficulty: sessionSettings.difficulty
      });

      if (response.data.success) {
        const newSessionData = response.data.data;
        setSessionData(newSessionData);
        setAllSessionsData(prev => [...prev, newSessionData]);
        
        // بدء الجلسة مباشرة
        try {
          const startResponse = await api.post(`/education/flash-anzan/sessions/${newSessionData.sessionId}/start`);
          
          if (startResponse.data.success) {
            const updatedSessionData = startResponse.data.data;
            setSessionData(updatedSessionData);
            setStudentAnswer('');
            setGameState('playing');
            setCurrentNumberIndex(0);
            startNumberDisplay(updatedSessionData);
            const roundToShow = nextRoundNum != null ? nextRoundNum : currentRound + 1;
            toast.success(`${t('flashCards.round')} ${roundToShow} ${t('flashCards.sessionReady')}!`);
          }
        } catch (startError) {
          console.error('Error starting session:', startError);
          toast.error(startError.response?.data?.message || 'Failed to start session');
          setGameState('ready');
        }
      }
    } catch (error) {
      console.error('Error creating next session:', error);
      toast.error(error.response?.data?.message || 'Failed to create next session');
    } finally {
      setIsLoading(false);
    }
  };

  // إعادة التشغيل
  const resetGame = () => {
    trafficLightTimeoutsRef.current.forEach((t) => clearTimeout(t));
    trafficLightTimeoutsRef.current = [];
    if (breakCountdownIntervalRef.current) {
      clearInterval(breakCountdownIntervalRef.current);
      breakCountdownIntervalRef.current = null;
    }
    setBreakCountdown(null);
    setBreakPaused(false);
    setTrafficLightIndex(0);
    setGameState('settings');
    setSessionData(null);
    setCurrentNumberIndex(0);
    setStudentAnswer('');
    setResult(null);
    setSessionStartTime(null);
    setCurrentRound(1);
    setRoundResults([]);
    setAllSessionsData([]);
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  };

  // تنظيف عند إلغاء التحميل
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      trafficLightTimeoutsRef.current.forEach((t) => clearTimeout(t));
      trafficLightTimeoutsRef.current = [];
      if (breakCountdownIntervalRef.current) {
        clearInterval(breakCountdownIntervalRef.current);
        breakCountdownIntervalRef.current = null;
      }
      const ctx = audioContextRef.current;
      if (ctx && ctx.state !== 'closed') {
        void ctx.close();
      }
      audioContextRef.current = null;
    };
  }, []);

  // تشغيل الصوت المناسب عند ظهور النتيجة النهائية
  useEffect(() => {
    if (gameState === 'result' && roundResults.length > 0) {
      const correctCount = roundResults.filter((r) => r.result?.isCorrect).length;
      const accuracy = (correctCount / roundResults.length) * 100;
      if (accuracy >= 80) {
        playSuccessSound();
      } else {
        playEncouragementSound();
      }
    }
  }, [gameState, roundResults]);

  // جلب بيانات الأرشيف
  const fetchArchiveData = async (page = 1) => {
    setLoadingArchive(true);
    try {
      const response = await api.get(`/education/flash-anzan/sessions?page=${page}&limit=10&status=completed`);
      if (response.data.success) {
        // تصفية البيانات للتأكد من عرض فقط الجلسات المكتملة
        const completedSessions = response.data.data.filter(session => session.status === 'completed');
        setArchiveData(completedSessions);
        // تحديث pagination إذا كانت البيانات مصفاة
        if (completedSessions.length !== response.data.data.length) {
          setArchivePagination({
            ...response.data.pagination,
            totalCount: completedSessions.length
          });
        } else {
          setArchivePagination(response.data.pagination);
        }
      }
    } catch (error) {
      console.error('Error fetching archive data:', error);
      toast.error('Failed to load archive data');
    } finally {
      setLoadingArchive(false);
    }
  };

  // جلب الإحصائيات العامة
  const fetchOverallStatistics = async () => {
    setLoadingStats(true);
    try {
      const response = await api.get('/education/flash-anzan/statistics/overall');
      if (response.data.success) {
        const data = response.data.data;
        setOverallStats({
          totalSessions: data.total_sessions,
          correctSessions: parseInt(data.correct_sessions),
          avgScore: parseFloat(data.avg_score),
          avgTime: parseFloat(data.avg_time_ms),
          bestScore: parseFloat(data.best_score),
          worstScore: parseFloat(data.worst_score),
          totalScore: parseFloat(data.total_score),
          accuracyPercentage: parseFloat(data.accuracy_percentage)
        });
      }
    } catch (error) {
      console.error('Error fetching overall statistics:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // جلب البيانات عند تحميل المكون
  useEffect(() => {
    fetchArchiveData();
    fetchOverallStatistics();
  }, []);

  // جلب مستوى الطالب للتحقق من نوع العمليات المسموح بها
  // دعم لوحة المفاتيح في شاشة الإجابة
  useEffect(() => {
    if (gameState !== 'answer') return;
    
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && studentAnswer) {
        submitAnswer();
      } else if (e.key === 'Backspace') {
        e.preventDefault();
        setStudentAnswer(prev => prev.slice(0, -1));
      } else if (/^\d$/.test(e.key)) {
        setStudentAnswer(prev => {
          const newValue = prev + e.key;
          return /^-?\d*$/.test(newValue) ? newValue : prev;
        });
      } else if (e.key === '-' && !studentAnswer.startsWith('-')) {
        setStudentAnswer(prev => prev === '' ? '-' : '-' + prev);
      }
    };
    
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [gameState, studentAnswer]);


  // عرض شاشة الإعدادات
  if (gameState === 'settings') {
  return (
      <div className="mx-auto w-full max-w-6xl space-y-5 px-3 pb-8 sm:space-y-6 sm:px-4 sm:pb-10" dir={isRTL ? 'rtl' : 'ltr'}>
        <DailyExercisesHero
          title={t('navigation.flashAnzan')}
          description={t('flashAnzan.pageDescription')}
          icon={ZapIcon}
          iconClassName="text-violet-600"
        />

      {/* إعدادات بدء الجلسة */}
      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/90 shadow-[0_8px_0_0_rgba(15,23,42,0.06)] backdrop-blur-sm">
        <div className="border-b border-slate-200/90 bg-gradient-to-r from-violet-50/90 via-white to-amber-50/50 px-4 py-3 sm:px-5">
          <h3 className="text-base font-black text-slate-900 sm:text-lg">
            {t('flashAnzan.sessionSettings')}
          </h3>
          <p className="mt-1 text-xs font-semibold text-slate-500 sm:text-sm">{t('flashAnzan.operationRestrictedByLevel')}</p>
        </div>
        <div className="p-4 sm:p-6">
               {/* صفان × 3 أعمدة: يمنع ضغط 6 حقول في صف واحد ضيق */}
               <div className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 lg:gap-x-5 lg:gap-y-4">
                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashAnzan.totalNumbers')}
                   </label>
                   <input
                     type="number"
                     min="1"
                     max="50"
                     className={kidInputClass}
                     value={sessionSettings.totalNumbers}
                     onChange={(e) => handleSettingChange('totalNumbers', e.target.value)}
                     placeholder="3"
                   />
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashAnzan.digitsPerNumber')}
                   </label>
                   <input
                     type="number"
                     min="1"
                     max="6"
                     className={kidInputClass}
                     value={sessionSettings.digitsPerNumber}
                     onChange={(e) => handleSettingChange('digitsPerNumber', e.target.value)}
                     placeholder="1"
                   />
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashAnzan.displayTime')} (ms)
                   </label>
                   <input
                     type="number"
                     min="500"
                     max="10000"
                     step="100"
                     className={kidInputClass}
                     value={sessionSettings.displayTime}
                     onChange={(e) => handleSettingChange('displayTime', e.target.value)}
                     placeholder="1500"
                   />
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashAnzan.operationType')}
                   </label>
                   <select
                     className={`${kidInputClass} max-w-full`}
                     value={sessionSettings.operationType}
                     onChange={(e) => handleSettingChange('operationType', e.target.value)}
                   >
                     <option value="addition">{t('flashAnzan.addition')}</option>
                     <option value="subtraction" disabled={disableOtherOperations}>{t('flashAnzan.subtraction')}</option>
                     <option value="mixed">{t('flashAnzan.mixed')}</option>
                     <option value="multiplication" disabled={disableOtherOperations}>{t('flashAnzan.multiplication')}</option>
                     <option value="division" disabled={disableOtherOperations}>{t('flashAnzan.division')}</option>
                   </select>
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashCards.totalRounds')}
                   </label>
                   <input
                     type="number"
                     min="1"
                     max="10"
                     className={kidInputClass}
                     value={sessionSettings.totalRounds}
                     onChange={(e) => handleSettingChange('totalRounds', e.target.value)}
                     placeholder="3"
                   />
                 </div>

                 <div className="min-w-0">
                   <label className={kidFieldLabel}>
                     {t('flashAnzan.difficulty')}
                   </label>
                   <select
                     className={`${kidInputClass} max-w-full`}
                     value={sessionSettings.difficulty}
                     onChange={(e) => handleSettingChange('difficulty', e.target.value)}
                   >
                     <option value="easy">{t('flashAnzan.difficultyEasy')}</option>
                     <option value="medium">{t('flashAnzan.difficultyMedium')}</option>
                     <option value="hard">{t('flashAnzan.difficultyHard')}</option>
                   </select>
                 </div>
               </div>

               <div className="flex justify-center">
                 <button
                   type="button"
                   onClick={createSession}
                   disabled={isLoading}
                   className="inline-flex min-h-[3.25rem] items-center gap-3 rounded-2xl border-[3px] border-sky-800/25 bg-gradient-to-b from-sky-400 to-sky-600 px-10 py-4 text-lg font-black text-white shadow-[0_4px_0_0_rgba(15,23,42,0.25)] transition hover:brightness-105 disabled:cursor-not-allowed disabled:opacity-50"
                 >
                   <PlayIcon className="h-7 w-7 shrink-0" strokeWidth={2.5} />
                   {isLoading ? t('flashAnzan.creating') : t('flashAnzan.startSession')}
                 </button>
               </div>
             </div>
      </section>

        {/* البلوكات الإحصائية — عمودان على الشاشات المتوسطة، أربعة فقط من xl فما فوق */}
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 sm:gap-5 xl:grid-cols-4">
          <div className="min-w-0 rounded-[1.35rem] border-[3px] border-sky-200/80 bg-gradient-to-br from-sky-50/95 to-white p-4 shadow-[0_8px_0_0_rgba(14,165,233,0.1)] sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-sky-200 bg-white">
                <svg className="h-6 w-6 text-sky-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 text-end">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('flashAnzan.totalSessions')}</p>
                <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">{loadingStats ? '…' : overallStats.totalSessions}</p>
              </div>
            </div>
            <div className="rounded-xl border border-sky-100 bg-sky-50/80 px-3 py-2">
              <p className="break-words text-xs font-semibold leading-snug text-sky-900/90">{t('flashAnzan.totalSessionsDescription')}</p>
            </div>
          </div>

          <div className="min-w-0 rounded-[1.35rem] border-[3px] border-emerald-200/80 bg-gradient-to-br from-emerald-50/95 to-white p-4 shadow-[0_8px_0_0_rgba(16,185,129,0.1)] sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-emerald-200 bg-white">
                <svg className="h-6 w-6 text-emerald-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 text-end">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('flashAnzan.accuracy')}</p>
                <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">{loadingStats ? '…' : `${overallStats.accuracyPercentage.toFixed(1)}%`}</p>
              </div>
            </div>
            <div className="rounded-xl border border-emerald-100 bg-emerald-50/80 px-3 py-2">
              <p className="break-words text-xs font-semibold leading-snug text-emerald-900/90">
                {overallStats.correctSessions} {t('flashAnzan.outOf')} {overallStats.totalSessions} {t('flashAnzan.sessionsCorrect')}
              </p>
            </div>
          </div>

          <div className="min-w-0 rounded-[1.35rem] border-[3px] border-amber-200/80 bg-gradient-to-br from-amber-50/95 to-white p-4 shadow-[0_8px_0_0_rgba(245,158,11,0.12)] sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-amber-200 bg-white">
                <svg className="h-6 w-6 text-amber-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 text-end">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('flashAnzan.bestScore')}</p>
                <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">{loadingStats ? '…' : overallStats.bestScore.toFixed(2)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-amber-100 bg-amber-50/80 px-3 py-2">
              <p className="break-words text-xs font-semibold text-amber-950/90">{t('flashAnzan.avgScore')}: {overallStats.avgScore.toFixed(2)}</p>
            </div>
          </div>

          <div className="min-w-0 rounded-[1.35rem] border-[3px] border-violet-200/80 bg-gradient-to-br from-violet-50/95 to-white p-4 shadow-[0_8px_0_0_rgba(139,92,246,0.1)] sm:p-5">
            <div className="mb-3 flex items-start justify-between gap-3">
              <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border-2 border-violet-200 bg-white">
                <svg className="h-6 w-6 text-violet-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                </svg>
              </div>
              <div className="min-w-0 flex-1 text-end">
                <p className="text-xs font-bold uppercase tracking-wide text-slate-500">{t('flashAnzan.totalScore')}</p>
                <p className="text-2xl font-black tabular-nums text-slate-900 sm:text-3xl">{loadingStats ? '…' : overallStats.totalScore.toFixed(2)}</p>
              </div>
            </div>
            <div className="rounded-xl border border-violet-100 bg-violet-50/80 px-3 py-2">
              <p className="break-words text-xs font-semibold leading-snug text-violet-950/90">{t('flashAnzan.totalScoreDescription')}</p>
            </div>
          </div>
        </div>

      {/* جدول أرشيف الجلسات */}
      <section className="overflow-hidden rounded-[1.35rem] border-[3px] border-slate-900/10 bg-white/90 shadow-[0_8px_0_0_rgba(15,23,42,0.06)]">
          <div className="border-b border-slate-200/90 bg-gradient-to-r from-sky-50/90 via-white to-amber-50/50 px-4 py-4 sm:px-6">
            <h3 className="text-base font-black text-slate-900 sm:text-lg">
              {t('flashAnzan.archive')}
            </h3>
            <p className="mt-1 text-xs font-semibold text-slate-600 sm:text-sm">
              {t('flashAnzan.archiveDescription')}
        </p>
      </div>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-slate-100">
              <thead className="bg-slate-50/90">
                <tr>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashAnzan.sessionId')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashAnzan.status')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashAnzan.operation')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashAnzan.numbers')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashAnzan.result')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashAnzan.correct')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashAnzan.score')}
                  </th>
                  <th className="px-4 py-3 text-start text-[11px] font-extrabold uppercase tracking-wide text-slate-500 sm:px-6 sm:text-xs">
                    {t('flashAnzan.displayTime')}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 bg-white">
                {loadingArchive ? (
                  <tr>
                    <td colSpan="8" className="px-4 py-6 text-center text-sm font-semibold text-slate-500 sm:px-6">
                      {t('flashAnzan.loading')}
                    </td>
                  </tr>
                ) : archiveData.length > 0 ? (
                  archiveData.map((session, index) => (
                    <tr key={index} className="transition-colors hover:bg-sky-50/60">
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold text-slate-900 sm:px-6">
                        {session.sessionId}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 sm:px-6">
                        <span className={`inline-flex rounded-full px-2.5 py-0.5 text-xs font-bold ${
                          session.status === 'completed' 
                            ? 'bg-green-100 text-green-800' 
                            : session.status === 'in_progress'
                            ? 'bg-yellow-100 text-yellow-800'
                            : session.status === 'created'
                            ? 'bg-blue-100 text-blue-800'
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {session.status === 'completed' ? t('flashAnzan.statusCompleted') :
                           session.status === 'in_progress' ? t('flashAnzan.statusInProgress') :
                           session.status === 'created' ? t('flashAnzan.statusCreated') :
                           t('flashAnzan.statusCancelled')}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-medium text-slate-800 sm:px-6">
                        {t(`flashAnzan.${session.operation}`)}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm text-slate-800 sm:px-6">
                        <span className="font-bold">{session.totalNumbers}:</span>
                        <span className="ms-1 text-slate-600">{session.numbers.join(isRTL ? '، ' : ', ')}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm font-semibold tabular-nums text-slate-900 sm:px-6">
                        {session.result}
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-start sm:px-6">
                        <span className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-xs font-bold ${
                          session.isCorrect === 1 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-red-100 text-red-800'
                        }`}>
                          {session.isCorrect === 1 ? (
                            <>
                              <CheckCircleIcon className="me-1 h-3 w-3 shrink-0" />
                              {t('flashAnzan.correct')}
                            </>
                          ) : (
                            <>
                              <XCircleIcon className="me-1 h-3 w-3 shrink-0" />
                              {t('flashAnzan.incorrect')}
                            </>
                          )}
                        </span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm sm:px-6">
                        <span className="font-black text-sky-600 tabular-nums">{session.score}</span>
                      </td>
                      <td className="whitespace-nowrap px-4 py-3 text-sm tabular-nums text-slate-700 sm:px-6">
                        {session.displayTime}ms
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="8" className="px-4 py-8 text-center text-sm font-semibold text-slate-500 sm:px-6">
                      {t('flashAnzan.noSessionsYet')}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {archivePagination.totalPages > 1 && (
            <div className="flex flex-col gap-3 border-t border-slate-200/90 bg-slate-50/50 px-4 py-4 sm:flex-row sm:items-center sm:justify-between sm:px-6">
              <div className="flex flex-1 justify-between gap-2 sm:hidden">
                <button
                  type="button"
                  onClick={() => fetchArchiveData(archivePagination.currentPage - 1)}
                  disabled={!archivePagination.hasPrevPage}
                  className="inline-flex min-h-[2.5rem] flex-1 items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('flashAnzan.previous')}
                </button>
                <button
                  type="button"
                  onClick={() => fetchArchiveData(archivePagination.currentPage + 1)}
                  disabled={!archivePagination.hasNextPage}
                  className="inline-flex min-h-[2.5rem] flex-1 items-center justify-center rounded-xl border-2 border-slate-200 bg-white px-3 text-sm font-bold text-slate-700 shadow-sm transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('flashAnzan.next')}
                </button>
              </div>
              <div className="hidden sm:flex sm:flex-1 sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm font-semibold text-slate-600">
                    {t('flashAnzan.showing')} <span className="font-medium">{((archivePagination.currentPage - 1) * archivePagination.limit) + 1}</span> {t('flashAnzan.to')} <span className="font-medium">{Math.min(archivePagination.currentPage * archivePagination.limit, archivePagination.totalCount)}</span> {t('flashAnzan.of')} <span className="font-medium">{archivePagination.totalCount}</span> {t('flashAnzan.results')}
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex overflow-hidden rounded-xl border-2 border-slate-200 bg-white shadow-sm" aria-label="Pagination">
                    <button
                      type="button"
                      onClick={() => fetchArchiveData(archivePagination.currentPage - 1)}
                      disabled={!archivePagination.hasPrevPage}
                      className="relative inline-flex items-center px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('flashAnzan.previous')}
                    </button>
                    
                    {/* ترقيم الصفحات */}
                    {Array.from({ length: archivePagination.totalPages }, (_, i) => i + 1).map((page) => {
                      // عرض الصفحات القريبة من الصفحة الحالية
                      const isNearCurrentPage = Math.abs(page - archivePagination.currentPage) <= 2;
                      const isFirstPage = page === 1;
                      const isLastPage = page === archivePagination.totalPages;
                      
                      if (isNearCurrentPage || isFirstPage || isLastPage) {
                        return (
                          <button
                            type="button"
                            key={page}
                            onClick={() => fetchArchiveData(page)}
                            className={`relative inline-flex min-w-[2.5rem] items-center justify-center border-s border-slate-200 px-3 py-2 text-sm font-bold ${
                              page === archivePagination.currentPage
                                ? 'z-10 bg-gradient-to-b from-sky-400 to-sky-600 text-white'
                                : 'text-slate-600 hover:bg-sky-50'
                            }`}
                          >
                            {page}
                          </button>
                        );
                      } else if (page === archivePagination.currentPage - 3 || page === archivePagination.currentPage + 3) {
                        // عرض نقاط الحذف
                        return (
                          <span key={`dots-${page}`} className="inline-flex items-center border-s border-slate-200 px-2 py-2 text-sm font-bold text-slate-400">
                            …
                          </span>
                        );
                      }
                      return null;
                    })}
                    
                    <button
                      type="button"
                      onClick={() => fetchArchiveData(archivePagination.currentPage + 1)}
                      disabled={!archivePagination.hasNextPage}
                      className="relative inline-flex items-center border-s border-slate-200 px-3 py-2 text-sm font-bold text-slate-600 transition hover:bg-sky-50 disabled:cursor-not-allowed disabled:opacity-50"
                    >
                      {t('flashAnzan.next')}
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
      </section>
       </div>
     );
   }

  // شاشة جاهز للبدء - في مودال
  if (gameState === 'ready') {
    return (
         /* Modal للشاشة الجاهزة */
         <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
          <div className="text-center">
            <div className="mb-8">
              <ZapIcon className="h-24 w-24 text-yellow-400 mx-auto mb-4" />
              <h1 className="text-4xl font-bold text-white mb-4">
                {t('navigation.flashAnzan')}
              </h1>
              <p className="text-xl text-gray-300 mb-2">
                {t('flashAnzan.sessionReady')}
              </p>
              <div className="text-gray-400 space-y-1">
                <p>{sessionData.totalNumbers} {t('flashAnzan.numbers')}</p>
                <p>{sessionData.displayTime}ms {t('flashAnzan.displayTime')}</p>
                <p>{t(`flashAnzan.${sessionData.operation}`)}</p>
              </div>
            </div>
            
            <button
              onClick={startSession}
              disabled={isLoading}
              className="px-12 py-4 bg-yellow-500 hover:bg-yellow-600 text-black font-bold text-xl rounded-lg transition-colors disabled:opacity-50"
            >
              {isLoading ? t('flashAnzan.starting') : t('flashAnzan.startNow')}
            </button>
          </div>
        </div>
    );
  }

   // شاشة إشارة المرور - إعداد قبل أول رقم (الجولة الأولى فقط)
   if (gameState === 'countdown' && sessionData) {
     const lights = [
       { color: 'bg-yellow-500', shadow: 'shadow-yellow-500/50' },
       { color: 'bg-orange-500', shadow: 'shadow-orange-500/50' },
       { color: 'bg-green-500', shadow: 'shadow-green-500/50' }
     ];
     return (
       <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
         <div className="flex flex-col items-center gap-8">
           <p className="text-gray-400 text-lg mb-4">{t('dailyExercises.getReady')}</p>
           <div className="flex gap-8">
             {lights.map((light, i) => (
               <div
                 key={i}
                 className={`w-20 h-20 rounded-full border-4 border-gray-700 transition-all duration-500 ${
                   trafficLightIndex === i + 1
                     ? `${light.color} ${light.shadow} shadow-2xl scale-110`
                     : 'bg-gray-800'
                 }`}
               />
             ))}
           </div>
         </div>
       </div>
     );
   }

   // شاشة الاستراحة بعد كل 10 جولات
   if (gameState === 'break') {
     const handleTakeBreak = () => {
       if (breakCountdownIntervalRef.current) {
         clearInterval(breakCountdownIntervalRef.current);
         breakCountdownIntervalRef.current = null;
       }
       breakPausedRef.current = true;
       setBreakPaused(true);
     };
     return (
       <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto bg-black px-4 py-4 sm:overflow-hidden">
         <div className="text-center max-w-2xl w-full px-6">
           <div className="mb-8">
             <div className="text-8xl mb-4">☕</div>
             <h2 className="text-4xl font-bold text-white mb-4">
               {t('dailyExercises.breakAfter10Rounds')}
             </h2>
             {!breakPaused ? (
               <>
                 <p className="text-xl text-gray-300 mb-4">
                   {t('dailyExercises.nextRoundInCountdown')}
                 </p>
                 <p className="text-5xl font-bold text-primary-400 mb-6 tabular-nums">
                   {breakCountdown != null ? breakCountdown : 6}
                 </p>
                 <button
                   type="button"
                   onClick={handleTakeBreak}
                   className="px-6 py-3 bg-amber-500 hover:bg-amber-600 text-white font-semibold rounded-lg transition-colors"
                 >
                   {t('dailyExercises.takeBreak')}
                 </button>
               </>
             ) : (
               <>
                 <p className="text-xl text-gray-300 mb-6">
                   {t('dailyExercises.breakPausedMessage')}
                 </p>
                 <button
                   type="button"
                   onClick={startNextRoundAfterBreak}
                   className="px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors"
                 >
                   {t('dailyExercises.continueOrResume')}
                 </button>
               </>
             )}
           </div>
         </div>
       </div>
     );
   }

   // شاشة اللعب - عرض الأرقام في مودال
   if (gameState === 'playing') {
     const currentNumber = sessionData?.numbers?.[currentNumberIndex] || '';

     return (
         /* Modal لعرض الأرقام */
         <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] flex-col overflow-hidden bg-black px-3 py-2">
           <div className="flex min-h-0 flex-1 flex-col items-center justify-center overflow-hidden px-2">
           <div className="text-center w-full max-w-[min(100vw,56rem)] mx-auto flex min-h-0 flex-1 flex-col items-center justify-center">
             <FullscreenNumberDisplay value={currentNumber} tickKey={currentNumberIndex} />
             <div className="mt-4 shrink-0 text-base text-gray-400 sm:text-lg">
               {currentNumberIndex + 1} / {sessionData?.numbers?.length}
             </div>
           </div>
           </div>
         </div>
     );
   }

   // لوحة الأرقام للإدخال
   const handleNumpadInput = (key) => {
     if (key === 'backspace') {
       setStudentAnswer(prev => prev.slice(0, -1));
     } else if (key === 'clear') {
       setStudentAnswer('');
     } else if (key === '-') {
       if (studentAnswer === '') {
         setStudentAnswer('-');
       } else if (studentAnswer === '-') {
         setStudentAnswer('');
       } else if (!studentAnswer.startsWith('-')) {
         setStudentAnswer('-' + studentAnswer);
       }
     } else {
       const newValue = studentAnswer + key;
       if (/^-?\d*$/.test(newValue)) {
         setStudentAnswer(newValue);
       }
     }
   };

   // شاشة إدخال الإجابة في مودال
   if (gameState === 'answer') {
     const numpadKeys = [
       ['7', '8', '9'],
       ['4', '5', '6'],
       ['1', '2', '3'],
       ['-', '0', 'backspace']
     ];

     return (
         /* Modal لإدخال الإجابة */
         <div className="fixed inset-0 z-[100] flex h-[100dvh] max-h-[100dvh] items-center justify-center overflow-y-auto overscroll-contain bg-black px-4 py-3">
           <div className="w-full max-w-md px-4 text-center">
             <div className="mb-6">
               <CheckCircleIcon className="h-16 w-16 text-green-400 mx-auto mb-4" />
               <h2 className="text-3xl font-bold text-white mb-4">
                 {t('flashAnzan.enterAnswer')}
               </h2>
               <p className="text-gray-300 mb-4">
                 {t('flashAnzan.calculateResult')}
               </p>
             </div>
             
             {/* حقل عرض الإجابة */}
             <div className="mb-4">
               <div
                 className="w-full px-6 py-4 text-3xl text-center bg-gray-800 text-white border-2 border-gray-600 rounded-lg min-h-[60px] flex items-center justify-center"
               >
                 {studentAnswer || <span className="text-gray-500">{t('flashAnzan.yourAnswer')}</span>}
               </div>
             </div>

             {/* لوحة الأرقام */}
             <div className="mb-6 bg-gray-900 rounded-xl p-4">
               <div className="grid gap-2">
                 {numpadKeys.map((row, rowIndex) => (
                   <div key={rowIndex} className="flex gap-2 justify-center">
                     {row.map((key) => (
                       <button
                         key={key}
                         type="button"
                         onClick={() => handleNumpadInput(key)}
                         className={`
                           w-16 h-14 text-2xl font-bold rounded-lg transition-all
                           ${key === 'backspace'
                             ? 'bg-amber-600 hover:bg-amber-500 text-white'
                             : key === '-'
                             ? 'bg-gray-600 hover:bg-gray-500 text-white'
                             : 'bg-gray-700 hover:bg-gray-600 text-white active:scale-95'
                           }
                         `}
                       >
                         {key === 'backspace' ? (
                           <svg className="w-6 h-6 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                             <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2M3 12l2-2m0 0L7 8m-4 4l2-2m0 0l2-2" />
                           </svg>
                         ) : (
                           key
                         )}
                       </button>
                     ))}
                   </div>
                 ))}
               </div>
               {/* زر مسح الكل */}
               <button
                 type="button"
                 onClick={() => handleNumpadInput('clear')}
                 className="w-full mt-2 py-2 text-sm text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
               >
                 {t('flashAnzan.clear')}
               </button>
             </div>
             
             <button
               onClick={submitAnswer}
               disabled={isLoading || !studentAnswer}
               className="w-full px-8 py-4 bg-green-500 hover:bg-green-600 text-white font-bold text-xl rounded-lg transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
             >
               {isLoading ? t('flashAnzan.submitting') : t('flashAnzan.submitAnswer')}
             </button>
           </div>
         </div>
     );
   }

   // شاشة النتيجة النهائية (مثل التمارين اليومية - ملخص كامل مع تفاصيل كل جولة)
   if (gameState === 'result' && roundResults.length > 0) {
     const totalScore = roundResults.reduce((sum, r) => sum + (r.result?.score || 0), 0);
     const totalTimeMs = roundResults.reduce((sum, r) => sum + (r.result?.totalTimeMs || 0), 0);
     const totalRounds = roundResults.length;
     const correctRounds = roundResults.filter((r) => r.result?.isCorrect).length;
     const accuracy = totalRounds > 0 ? (correctRounds / totalRounds) * 100 : 0;

     let message = '';
     let emoji = '';
     let messageColor = '';
     if (accuracy >= 90) {
       message = t('dailyExercises.excellentResult');
       emoji = '🎉🎉🎉';
       messageColor = 'text-green-400';
     } else if (accuracy >= 70) {
       message = t('dailyExercises.goodResult');
       emoji = '🎉🎉';
       messageColor = 'text-green-300';
     } else if (accuracy >= 50) {
       message = t('dailyExercises.fairResult');
       emoji = '👍';
       messageColor = 'text-yellow-400';
     } else {
       message = t('dailyExercises.keepPracticing');
       emoji = '💪';
       messageColor = 'text-orange-400';
     }

     return (
       <div className="fixed inset-0 z-[100] max-h-[100dvh] overflow-y-auto bg-black">
         <div className="flex min-h-full flex-col items-center px-3 pb-16 pt-6 md:pb-24 md:pt-8">
           <div className="text-center max-w-2xl w-full px-6">
           <div className="mb-8">
             <div className="text-8xl mb-4">{emoji}</div>
             <h2 className={`text-4xl font-bold mb-4 ${messageColor}`}>{message}</h2>
             
             <div className="bg-gray-800 rounded-lg p-6 mb-6">
               <div className="grid grid-cols-2 gap-4 text-white mb-4">
                 <div>
                   <p className="text-gray-400 text-sm mb-1">{t('dailyExercises.totalScore')}</p>
                   <p className="text-3xl font-bold text-primary-400">{totalScore.toFixed(1)}</p>
                 </div>
                 <div>
                   <p className="text-gray-400 text-sm mb-1">{t('dailyExercises.totalTime')}</p>
                   <p className="text-3xl font-bold text-primary-400">{(totalTimeMs / 1000).toFixed(1)}s</p>
                 </div>
                 <div>
                   <p className="text-gray-400 text-sm mb-1">{t('dailyExercises.correctRounds')}</p>
                   <p className="text-3xl font-bold text-green-400">{correctRounds} / {totalRounds}</p>
                 </div>
                 <div>
                   <p className="text-gray-400 text-sm mb-1">{t('dailyExercises.accuracy')}</p>
                   <p className="text-3xl font-bold text-yellow-400">{accuracy.toFixed(1)}%</p>
                 </div>
               </div>

               <div className="mt-4 pt-4 border-t border-gray-700">
                 <h3 className="text-lg font-semibold text-white mb-3">{t('dailyExercises.roundDetails')}</h3>
                 <div className="space-y-2 text-sm">
                   {roundResults.map((r, index) => (
                     <div key={index} className="flex items-center justify-between text-gray-300">
                       <span>
                         {t('dailyExercises.round')} {index + 1}: {r.result?.studentAnswer ?? '-'}
                       </span>
                       <div className="flex items-center gap-2">
                         {r.result?.isCorrect ? (
                           <>
                             <CheckCircleIcon className="w-4 h-4 text-green-400" />
                             <span className="text-green-400">+{(r.result?.score ?? 0).toFixed(1)}</span>
                           </>
                         ) : (
                           <>
                             <XCircleIcon className="w-4 h-4 text-red-400" />
                             <span className="text-red-400">0</span>
                             <span className="text-gray-500 text-xs">({t('dailyExercises.correct')}: {r.result?.correctAnswer ?? '-'})</span>
                           </>
                         )}
                       </div>
                     </div>
                   ))}
                 </div>
               </div>
             </div>

             <button
               onClick={resetGame}
               className="flex items-center gap-2 px-6 py-3 bg-primary-500 hover:bg-primary-600 text-white font-semibold rounded-lg transition-colors mx-auto"
             >
               {t('flashAnzan.playAgain')}
             </button>
           </div>
           </div>
         </div>
       </div>
     );
   }

   return null;
};

export default FlashAnzan;
